/**
 * workflowContainerProjector.ts — LangchainAgentEvent → 容器树投影
 *
 * 职责：
 *     对齐 SDK workflow_container_projector；维护 active set；正文路由。
 *
 * 链路位置：
 *     workflowReducer（P1）← consume LangchainAgentEvent；fixture 单测 SSOT。
 *
 * 当前裁剪范围：
 *     仅投影层；不负责 React 渲染或 loop tree reduceTree。
 */
import type { LangchainAgentEvent } from '../../types/events';
import { readDisplayPayload } from '../../types/workflowDisplay';
import type {
  ContainerAction,
  WorkflowContainerNode,
  WorkflowContainerScope,
  WorkflowContainerStatus,
  WorkflowContainerTreeSnapshot,
  WorkflowContentBlock,
} from '../../types/workflowContainer';

const OPEN_EVENT_TYPES = new Set<string>([
  'workflow-start',
  'subworkflow-start',
  'stage-start',
  'parallel-item-start',
  'parallel-aggregate-start',
  'route-branch-start',
]);

const CLOSE_EVENT_TYPES = new Set<string>([
  'workflow-end',
  'workflow-failed',
  'subworkflow-end',
  'stage-done',
  'stage-failed',
  'parallel-item-done',
  'parallel-item-failed',
  'parallel-aggregate-done',
  'parallel-aggregate-failed',
  'route-branch-done',
  'route-branch-failed',
]);

const EVENT_TYPE_TO_SCOPE: Record<string, WorkflowContainerScope> = {
  'workflow-start': 'workflow',
  'workflow-end': 'workflow',
  'workflow-failed': 'workflow',
  'subworkflow-start': 'subworkflow',
  'subworkflow-end': 'subworkflow',
  'stage-start': 'stage',
  'stage-done': 'stage',
  'stage-failed': 'stage',
  'parallel-item-start': 'item',
  'parallel-item-done': 'item',
  'parallel-item-failed': 'item',
  'parallel-aggregate-start': 'aggregate',
  'parallel-aggregate-done': 'aggregate',
  'parallel-aggregate-failed': 'aggregate',
  'route-branch-start': 'branch',
  'route-branch-done': 'branch',
  'route-branch-failed': 'branch',
};

const CONTENT_EVENT_TYPES = new Set<string>([
  'reasoning-start',
  'reasoning-delta',
  'reasoning-end',
  'text-start',
  'text-delta',
  'text-end',
  'tool-input',
  'tool-input-start',
  'tool-input-delta',
  'tool-call',
  'tool-result',
  'tool-error',
  'tool-progress',
]);

const SCOPE_PRIORITY: Record<WorkflowContainerScope, number> = {
  workflow: 0,
  subworkflow: 1,
  aggregate: 2,
  stage: 3,
  item: 3,
  branch: 3,
};

const FAILED_CLOSE_TYPES = new Set<string>([
  'workflow-failed',
  'stage-failed',
  'parallel-item-failed',
  'parallel-aggregate-failed',
  'route-branch-failed',
]);

export function buildContainerId(
  workflowPath: string,
  scope: WorkflowContainerScope,
  scopeKey: string,
): string {
  return `${workflowPath}|${scope}:${scopeKey}`;
}

function readString(data: Record<string, unknown>, key: string): string | undefined {
  const value = data[key];
  return typeof value === 'string' ? value : undefined;
}

export function resolveScopeKey(eventType: string, data: Record<string, unknown>): string {
  if (
    eventType === 'workflow-start'
    || eventType === 'workflow-end'
    || eventType === 'workflow-failed'
  ) {
    const workflowId = readString(data, 'workflow_id');
    if (workflowId) return workflowId;
    return readString(data, 'workflow_path') ?? 'workflow';
  }

  if (eventType === 'subworkflow-start' || eventType === 'subworkflow-end') {
    return readString(data, 'child_workflow_id') ?? 'subworkflow';
  }

  if (
    eventType === 'stage-start'
    || eventType === 'stage-done'
    || eventType === 'stage-failed'
  ) {
    const stageKey = readString(data, 'stage_key');
    if (stageKey) return stageKey;
    const stageIndex = data.stage_index;
    return typeof stageIndex === 'number' ? String(stageIndex) : 'stage';
  }

  if (
    eventType === 'parallel-item-start'
    || eventType === 'parallel-item-done'
    || eventType === 'parallel-item-failed'
  ) {
    const itemKey = readString(data, 'item_key');
    if (itemKey) return itemKey;
    const itemIndex = data.item_index;
    return typeof itemIndex === 'number' ? String(itemIndex) : 'item';
  }

  if (
    eventType === 'parallel-aggregate-start'
    || eventType === 'parallel-aggregate-done'
    || eventType === 'parallel-aggregate-failed'
  ) {
    return readString(data, 'aggregate_key') ?? 'aggregate';
  }

  if (
    eventType === 'route-branch-start'
    || eventType === 'route-branch-done'
    || eventType === 'route-branch-failed'
  ) {
    return readString(data, 'branch_key') ?? readString(data, 'branch_node') ?? 'branch';
  }

  const display = readDisplayPayload(data);
  if (display?.debug_key) return display.debug_key;

  return 'unknown';
}

function displayStatus(
  data: Record<string, unknown>,
  fallback: WorkflowContainerStatus,
): WorkflowContainerStatus {
  const display = readDisplayPayload(data);
  if (display?.status) return display.status;
  return fallback;
}

function closeStatus(eventType: string): WorkflowContainerStatus {
  return FAILED_CLOSE_TYPES.has(eventType) ? 'failed' : 'completed';
}

function readPreview(data: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string') return value;
  }
  return '';
}

function contentBlockFromEvent(event: LangchainAgentEvent): WorkflowContentBlock | null {
  const { event_type: eventType } = event;
  const payload = (event.data ?? {}) as Record<string, unknown>;

  if (
    eventType === 'reasoning-start'
    || eventType === 'reasoning-delta'
    || eventType === 'reasoning-end'
  ) {
    let preview = readPreview(payload, 'delta', 'text', 'reasoning');
    if (!preview && eventType === 'reasoning-start') preview = 'Thinking…';
    return { kind: 'reasoning', preview, tool_name: null };
  }

  if (
    eventType === 'text-start'
    || eventType === 'text-delta'
    || eventType === 'text-end'
  ) {
    const preview = readPreview(payload, 'delta', 'text');
    return { kind: 'text', preview, tool_name: null };
  }

  if (CONTENT_EVENT_TYPES.has(eventType)) {
    const toolName = event.tool_name ?? readString(payload, 'tool_name');
    const preview =
      readPreview(payload, 'summary', 'output', 'input', 'delta')
      || toolName
      || eventType;
    return {
      kind: 'tool',
      preview,
      tool_name: toolName ?? null,
    };
  }

  return null;
}

export function resolveContentScopeKey(data: Record<string, unknown>): string | null {
  for (const keyName of ['stage_key', 'item_key', 'branch_key', 'aggregate_key', 'debug_key'] as const) {
    const value = readString(data, keyName);
    if (value) return value;
  }
  const display = readDisplayPayload(data);
  return display?.debug_key ?? null;
}

/** 按容器身份索引的活跃集（并发 item 不依赖 LIFO 单栈） */
export class WorkflowContainerActiveSet {
  private readonly openOrder: string[] = [];
  private readonly nodes: Record<string, WorkflowContainerNode> = {};

  restoreNode(node: WorkflowContainerNode): void {
    this.nodes[node.container_id] = { ...node };
    if (node.is_open && !this.openOrder.includes(node.container_id)) {
      this.openOrder.push(node.container_id);
    }
  }

  open(node: WorkflowContainerNode): void {
    this.nodes[node.container_id] = node;
    if (!this.openOrder.includes(node.container_id)) {
      this.openOrder.push(node.container_id);
    }
  }

  close(containerId: string, status: WorkflowContainerStatus): void {
    const node = this.nodes[containerId];
    if (!node) return;
    node.status = status;
    node.is_open = false;
    const idx = this.openOrder.indexOf(containerId);
    if (idx !== -1) this.openOrder.splice(idx, 1);
  }

  get activeIds(): string[] {
    return [...this.openOrder];
  }

  getNode(containerId: string): WorkflowContainerNode | undefined {
    return this.nodes[containerId];
  }

  get nodesById(): Record<string, WorkflowContainerNode> {
    return this.nodes;
  }

  rankContainer(containerId: string): [number, number, number, number] {
    const node = this.nodes[containerId];
    if (!node) return [-1, -1, -1, -1];
    const openIndex = this.openOrder.indexOf(containerId);
    const scopePriority = SCOPE_PRIORITY[node.scope] ?? 0;
    return [node.workflow_depth, scopePriority, node.workflow_path.length, openIndex];
  }

  deepestActive(): string | null {
    if (this.openOrder.length === 0) return null;
    return this.openOrder.reduce((best, cid) =>
      this.compareRank(cid, best) > 0 ? cid : best,
    );
  }

  resolveContentTarget(workflowPath: string | null, scopeKey: string | null): string | null {
    if (scopeKey) {
      for (let i = this.openOrder.length - 1; i >= 0; i -= 1) {
        const containerId = this.openOrder[i]!;
        const node = this.nodes[containerId]!;
        if (node.scope_key === scopeKey && node.is_open) {
          if (workflowPath === null || node.workflow_path === workflowPath) {
            return containerId;
          }
        }
      }
    }

    if (workflowPath) {
      const candidates = this.openOrder.filter((cid) => {
        const node = this.nodes[cid]!;
        if (!node.is_open) return false;
        return (
          node.workflow_path === workflowPath
          || workflowPath.startsWith(`${node.workflow_path}>`)
        );
      });
      if (candidates.length > 0) {
        return candidates.reduce((best, cid) =>
          this.compareRank(cid, best) > 0 ? cid : best,
        );
      }
    }

    return this.deepestActive();
  }

  closeDescendantsOfPath(workflowPath: string, status: WorkflowContainerStatus): void {
    const toClose = this.openOrder.filter((cid) => {
      const node = this.nodes[cid]!;
      return (
        node.workflow_path === workflowPath
        || node.workflow_path.startsWith(`${workflowPath}>`)
      );
    });
    for (const containerId of toClose) {
      this.close(containerId, status);
    }
  }

  private compareRank(a: string, b: string): number {
    const ra = this.rankContainer(a);
    const rb = this.rankContainer(b);
    for (let i = 0; i < ra.length; i += 1) {
      if (ra[i]! > rb[i]!) return 1;
      if (ra[i]! < rb[i]!) return -1;
    }
    return 0;
  }

  /** 供 StateMachine 比较容器深度 rank */
  compareContainerRank(a: string, b: string): number {
    return this.compareRank(a, b);
  }
}

/** 事件 → 容器状态机（对齐 SDK reference） */
export class WorkflowContainerStateMachine {
  private readonly active = new WorkflowContainerActiveSet();
  private readonly rootIds: string[] = [];
  private suppressContentContainerIds: ReadonlySet<string> = new Set();

  get activeSet(): WorkflowContainerActiveSet {
    return this.active;
  }

  setSuppressContentContainerIds(ids: ReadonlySet<string>): void {
    this.suppressContentContainerIds = ids;
  }

  rehydrate(snapshot: WorkflowContainerTreeSnapshot): void {
    this.rootIds.length = 0;
    this.rootIds.push(...snapshot.root_container_ids);
    for (const node of Object.values(snapshot.containers)) {
      this.active.restoreNode(node);
    }
  }

  apply(event: LangchainAgentEvent): ContainerAction | null {
    const eventType = event.event_type;
    const data = (event.data ?? {}) as Record<string, unknown>;

    if (OPEN_EVENT_TYPES.has(eventType)) {
      this.openContainer(eventType, data);
      return 'open';
    }

    if (CLOSE_EVENT_TYPES.has(eventType)) {
      this.closeContainer(eventType, data);
      return 'close';
    }

    if (CONTENT_EVENT_TYPES.has(eventType)) {
      this.appendContent(event);
      return 'append-content';
    }

    return null;
  }

  snapshot(): WorkflowContainerTreeSnapshot {
    return {
      root_container_ids: [...this.rootIds],
      containers: { ...this.active.nodesById },
      active_container_ids: this.active.activeIds,
    };
  }

  private openContainer(eventType: string, data: Record<string, unknown>): void {
    const scope = EVENT_TYPE_TO_SCOPE[eventType];
    if (!scope) return;

    const workflowPath = readString(data, 'workflow_path') ?? '';
    const workflowDepth =
      typeof data.workflow_depth === 'number' ? data.workflow_depth : 0;
    const scopeKey = resolveScopeKey(eventType, data);
    const containerId = buildContainerId(workflowPath, scope, scopeKey);

    const display = readDisplayPayload(data);
    const title = display?.title ?? scopeKey;
    const subtitle = display?.description ?? '';
    const pattern = display?.pattern ?? '';
    const status = displayStatus(data, 'running');
    const parentId = this.resolveParentId(workflowPath, scope);

    const node: WorkflowContainerNode = {
      container_id: containerId,
      scope,
      workflow_path: workflowPath,
      workflow_depth: workflowDepth,
      scope_key: scopeKey,
      title,
      subtitle,
      status,
      pattern,
      parent_container_id: parentId,
      display,
      content_blocks: [],
      is_open: true,
    };

    this.active.open(node);
    if (scope === 'workflow' && !this.rootIds.includes(containerId)) {
      this.rootIds.push(containerId);
    }
  }

  private closeContainer(eventType: string, data: Record<string, unknown>): void {
    const scope = EVENT_TYPE_TO_SCOPE[eventType];
    if (!scope) return;

    const workflowPath = readString(data, 'workflow_path') ?? '';
    const scopeKey = resolveScopeKey(eventType, data);
    const containerId = buildContainerId(workflowPath, scope, scopeKey);
    const status = displayStatus(data, closeStatus(eventType));

    this.active.close(containerId, status);
    if (scope === 'workflow') {
      this.active.closeDescendantsOfPath(workflowPath, status);
    }
  }

  private appendContent(event: LangchainAgentEvent): void {
    const data = (event.data ?? {}) as Record<string, unknown>;
    const workflowPath = readString(data, 'workflow_path') ?? null;
    const scopeKey = resolveContentScopeKey(data);

    const targetId = this.active.resolveContentTarget(workflowPath, scopeKey);
    if (!targetId) return;
    if (this.suppressContentContainerIds.has(targetId)) return;

    const block = contentBlockFromEvent(event);
    if (!block) return;

    const node = this.active.getNode(targetId);
    if (!node) return;

    const last = node.content_blocks[node.content_blocks.length - 1];
    if (
      last
      && last.kind === block.kind
      && (block.kind === 'reasoning' || block.kind === 'text')
    ) {
      if (block.preview) {
        if (block.kind === 'reasoning' && last.preview === 'Thinking…') {
          last.preview = block.preview;
        } else {
          last.preview = `${last.preview}${block.preview}`;
        }
      }
      return;
    }

    node.content_blocks.push(block);
  }

  private resolveParentId(
    workflowPath: string,
    scope: WorkflowContainerScope,
  ): string | null {
    if (scope === 'workflow') return null;

    const openNodes = this.active.activeIds
      .map((cid) => this.active.getNode(cid)!)
      .filter((node) => node.is_open);

    if (openNodes.length === 0) return null;

    const prefixCandidates = openNodes.filter(
      (node) =>
        workflowPath === node.workflow_path
        || workflowPath.startsWith(`${node.workflow_path}>`),
    );

    if (prefixCandidates.length > 0) {
      const parentCandidates = prefixCandidates.filter((node) => {
        // 并行 item 不应挂到已打开的 sibling item 下；否则第二路会误落到 workflow 根。
        if (scope === 'item') return node.scope !== 'item';
        if (scope === 'aggregate') return node.scope !== 'item' && node.scope !== 'aggregate';
        return node.scope !== scope;
      });
      if (parentCandidates.length > 0) {
        const parent = parentCandidates.reduce((best, node) =>
          this.active.compareContainerRank(node.container_id, best.container_id) > 0
            ? node
            : best,
        );
        if (parent.workflow_path === workflowPath && parent.scope === 'workflow') {
          return parent.container_id;
        }
        if (workflowPath.startsWith(`${parent.workflow_path}>`)) {
          return parent.container_id;
        }
        if (parent.workflow_path === workflowPath && parent.scope !== scope) {
          return parent.container_id;
        }
      }
    }

    const workflowCandidates = openNodes.filter((node) => node.scope === 'workflow');
    if (workflowCandidates.length > 0) {
      return workflowCandidates.reduce((best, node) =>
        this.active.compareContainerRank(node.container_id, best.container_id) > 0
          ? node
          : best,
      ).container_id;
    }

    return openNodes.reduce((best, node) =>
      this.active.compareContainerRank(node.container_id, best.container_id) > 0
        ? node
        : best,
    ).container_id;
  }
}

/** LangchainAgentEvent 流 → 容器树快照 */
export class WorkflowContainerTreeProjector {
  private readonly machine = new WorkflowContainerStateMachine();

  rehydrate(snapshot: WorkflowContainerTreeSnapshot): void {
    this.machine.rehydrate(snapshot);
  }

  setSuppressContentContainerIds(ids: ReadonlySet<string>): void {
    this.machine.setSuppressContentContainerIds(ids);
  }

  consume(event: LangchainAgentEvent): ContainerAction | null {
    return this.machine.apply(event);
  }

  consumeAll(events: LangchainAgentEvent[]): WorkflowContainerTreeSnapshot {
    for (const event of events) {
      this.consume(event);
    }
    return this.snapshot();
  }

  snapshot(): WorkflowContainerTreeSnapshot {
    return this.machine.snapshot();
  }
}

/** 纯函数：快照 + 单事件 → 新快照（供 workflowReducer 双轨归约） */
export interface ReduceContainerSnapshotOptions {
  /** 已绑定 loop 的容器不再写入 content_blocks（过程层走 AgentLoopView） */
  suppressContentContainerIds?: ReadonlySet<string>;
}

export function reduceContainerSnapshot(
  snapshot: WorkflowContainerTreeSnapshot,
  event: LangchainAgentEvent,
  options?: ReduceContainerSnapshotOptions,
): WorkflowContainerTreeSnapshot {
  const projector = new WorkflowContainerTreeProjector();
  projector.rehydrate(snapshot);
  if (options?.suppressContentContainerIds?.size) {
    projector.setSuppressContentContainerIds(options.suppressContentContainerIds);
  }
  projector.consume(event);
  return projector.snapshot();
}

/** 从 open 容器集解析 agent 正文路由目标 */
export function resolveContentTargetFromSnapshot(
  snapshot: WorkflowContainerTreeSnapshot,
  workflowPath: string | null,
  scopeKey: string | null,
): string | null {
  const active = new WorkflowContainerActiveSet();
  for (const containerId of snapshot.active_container_ids) {
    const node = snapshot.containers[containerId];
    if (node?.is_open) active.restoreNode(node);
  }
  return active.resolveContentTarget(workflowPath, scopeKey);
}

/** 投影快照合并 loopSessionId（projector 不管理 loop 绑定） */
export function mergeRuntimeContainerNodes(
  prev: Record<string, { loopSessionId: string | null }>,
  snapshot: WorkflowContainerTreeSnapshot,
): Record<
  string,
  WorkflowContainerNode & { loopSessionId: string | null }
> {
  const out: Record<string, WorkflowContainerNode & { loopSessionId: string | null }> = {};
  for (const [containerId, node] of Object.entries(snapshot.containers)) {
    out[containerId] = {
      ...node,
      loopSessionId: prev[containerId]?.loopSessionId ?? null,
    };
  }
  return out;
}

/** 序列化为 SDK fixture expected 对照结构 */
export function snapshotToExpectedFormat(
  snapshot: WorkflowContainerTreeSnapshot,
): {
  root_container_ids: string[];
  active_container_ids: string[];
  containers: Record<
    string,
    {
      title: string;
      subtitle: string;
      status: WorkflowContainerStatus;
      scope: WorkflowContainerScope;
      parent_container_id: string | null;
      content_blocks: WorkflowContentBlock[];
    }
  >;
} {
  const containers: Record<
    string,
    {
      title: string;
      subtitle: string;
      status: WorkflowContainerStatus;
      scope: WorkflowContainerScope;
      parent_container_id: string | null;
      content_blocks: WorkflowContentBlock[];
    }
  > = {};

  for (const [cid, node] of Object.entries(snapshot.containers)) {
    containers[cid] = {
      title: node.title,
      subtitle: node.subtitle,
      status: node.status,
      scope: node.scope,
      parent_container_id: node.parent_container_id,
      content_blocks: node.content_blocks.map((b) => ({
        kind: b.kind,
        preview: b.preview,
        tool_name: b.tool_name,
      })),
    };
  }

  return {
    root_container_ids: snapshot.root_container_ids,
    active_container_ids: snapshot.active_container_ids,
    containers,
  };
}

export type {
  WorkflowContainerNode,
  WorkflowContainerTreeSnapshot,
  WorkflowContentBlock,
  ContainerAction,
};
